import { usePage } from '@/components/providers/PageProvider'
import { type CardType } from '@/defs/card'
import { type IconType } from '@/defs/icon'
import { type RecType } from '@/defs/rec'
import { type RouteSectionType, type RouteType } from '@/defs/route'
import { useCardsRec } from '@/hooks/useCardsRec'
import { getCardFirstName, getCardRef, parseCardId } from '@/utils/cardUtil'
import { slugify } from '@/utils/commonUtil'
import { buildRec, getRecSlug } from '@/utils/recUtil'
import { buildRoute } from '@/utils/routeUtil'

type ConfigType = {
  icon: IconType
  label: string
  options: {
    check: boolean
    name?: string
    route: RouteType
  }[]
}

type Props = {
  mode: 'featured' | 'modal'
  rec?: RecType
}

const isCardPartnerable = (card?: CardType) =>
  !!(
    card?.isLegalPartner ||
    card?.isBackground ||
    card?.isBackgroundChooser ||
    card?.isDoctor ||
    card?.isDoctorsCompanion
  )

export const useCardRoleLinks = ({ mode, rec }: Props) => {
  const { page } = usePage()
  const { card, cardPartner } = useCardsRec({ rec })
  const currentSection = page?.route?.section || ''
  const isFeatured = mode === 'featured'

  const notCurrent = (section: RouteSectionType) => !isFeatured || currentSection !== section

  if (!rec) return []

  const { cardId, name, partnerRef } = rec
  const slugFull = getRecSlug(rec)
  const { slug } = parseCardId(cardId)
  const { slug: partnerSlug } = partnerRef ? parseCardId(partnerRef.cardId) : {}

  const isPartner = !!partnerRef

  const isPartnerable = isCardPartnerable(card)
  const isPartnerPartnerable = isCardPartnerable(cardPartner)

  const slugWithPartner = card?.partnersWith
    ? getRecSlug(buildRec({ ...rec, partnerRef: getCardRef({ name: card.partnersWith }) }))
    : undefined

  const links: ConfigType[] = [
    ...(isPartner
      ? ([
          {
            icon: 'cards',
            label: 'As Card',
            options: [
              {
                check: notCurrent('cardsFeatured'),
                name: getCardFirstName(name),
                route: { cardSlug: slug, section: 'cardsFeatured' },
              },
              {
                check: notCurrent('cardsFeatured'),
                name: getCardFirstName(partnerRef.name),
                route: { cardSlug: partnerSlug, section: 'cardsFeatured' },
              },
            ],
          },
          {
            icon: 'crown',
            label: 'As Commander',
            options: [
              {
                check: notCurrent('commandersFeatured'),
                route: { cardSlug: slugFull, section: 'commandersFeatured' },
              },
            ],
          },
          {
            icon: 'hatCowboy',
            label: 'As Partner',
            options: [
              {
                check: !card?.partnersWith && notCurrent('partnersFeatured') && isPartnerable,
                name: getCardFirstName(name),
                route: { cardSlug: slug, section: 'partnersFeatured' },
              },
              {
                check: !card?.partnersWith && notCurrent('partnersFeatured') && isPartnerPartnerable,
                name: getCardFirstName(partnerRef.name),
                route: { cardSlug: partnerSlug, section: 'partnersFeatured' },
              },
            ],
          },
        ] satisfies ConfigType[])
      : ([
          {
            icon: 'cards',
            label: 'As Card',
            options: [{ check: notCurrent('cardsFeatured'), route: { cardSlug: slug, section: 'cardsFeatured' } }],
          },
          {
            icon: 'crown',
            label: 'As Commander',
            options: [
              {
                check: notCurrent('commandersFeatured') && !!card?.isLegalCommander,
                route: { cardSlug: slug, section: 'commandersFeatured' },
              },
            ],
          },
          {
            icon: 'hatCowboy',
            label: 'As Partner',
            options: [
              {
                check: notCurrent('partnersFeatured') && isPartnerable,
                route: card?.partnersWith
                  ? { cardSlug: slugWithPartner, section: 'commandersFeatured' }
                  : { cardSlug: slug, section: 'partnersFeatured' },
              },
            ],
          },
        ] satisfies ConfigType[])),
    {
      icon: 'cube',
      label: 'View Precon',
      options: [
        {
          check: notCurrent('preconsFeatured') && !!card?.precon,
          route: {
            preconCommanderSlug: slugFull,
            preconSlug: card?.precon ? slugify(card.precon) : undefined,
            section: 'preconsFeatured',
          },
        },
      ],
    },
  ]

  return links
    .map(({ icon, label, options }) => ({
      icon,
      label,
      options: options
        .filter(({ check }) => check)
        .map((option) => ({
          href: buildRoute(option.route),
          label: option.name ? `${label} (${option.name})` : label,
          name: option.name,
        })),
    }))
    .filter(({ options }) => options.length)
}
